import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useCreatePolicyConfiguration, usePolicyConfigurationLookups } from './BenefitPolicyService'
import { ExistingDraftNotice } from './ExistingDraftNotice'
import { yupSiblingString } from '~/shared/lib/guards'

/**
 * Collect the four fields a policy configuration cannot exist without.
 *
 * This is a dialog rather than a blank editor because the row itself requires a contract, an
 * effective period and an electable start date. Everything else is filled in the editor afterwards.
 */
interface NewPolicyDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (created: { id: number; effective_start: string | null }) => void
}

const validationSchema = Yup.object({
  contract: Yup.number().required('Pick a contract.'),
  effective_start: Yup.string().required('An effective start date is required.'),
  effective_end: Yup.string()
    .required('An effective end date is required.')
    .test(
      'after-start',
      'The effective end date must be after the effective start date.',
      // Mirrors the server's own check so the operator is told before the round trip; the server
      // stays the authority and refuses the same case.
      (value, context) => {
        const start = yupSiblingString(context, 'effective_start')
        return !value || !start || value > start
      },
    ),
  electable_start_date: Yup.string().required('An electable start date is required.'),
})

export const NewPolicyDialog = ({ open, onClose, onCreated }: NewPolicyDialogProps) => {
  const lookups = usePolicyConfigurationLookups()
  const createPolicy = useCreatePolicyConfiguration()

  const formik = useFormik({
    initialValues: {
      contract: '',
      effective_start: '',
      effective_end: '',
      electable_start_date: '',
      description: '',
    },
    validationSchema,
    onSubmit: async values => {
      const created = await createPolicy.mutateAsync({
        contract: Number(values.contract),
        effective_start: values.effective_start,
        effective_end: values.effective_end,
        electable_start_date: values.electable_start_date,
        description: values.description || undefined,
      })
      formik.resetForm()
      onCreated({ id: created.id, effective_start: created.effective_start })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>New policy</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Opens a draft. Nothing is visible to members until it is published.
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              required
              name="contract"
              label="Contract"
              value={formik.values.contract}
              onChange={formik.handleChange}
              error={formik.touched.contract && !!formik.errors.contract}
              helperText={formik.touched.contract && formik.errors.contract}
              disabled={lookups.isPending}
              data-testid="new-policy-contract"
            >
              {(lookups.data?.contracts ?? []).map(contract => (
                <MenuItem key={contract.id} value={contract.id}>
                  {contract.name} — {contract.contracted_entity_label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                required
                fullWidth
                type="date"
                name="effective_start"
                label="Effective start"
                slotProps={{ inputLabel: { shrink: true } }}
                value={formik.values.effective_start}
                onChange={formik.handleChange}
                error={formik.touched.effective_start && !!formik.errors.effective_start}
                helperText={formik.touched.effective_start && formik.errors.effective_start}
                data-testid="new-policy-effective-start"
              />
              <TextField
                required
                fullWidth
                type="date"
                name="effective_end"
                label="Effective end"
                slotProps={{ inputLabel: { shrink: true } }}
                value={formik.values.effective_end}
                onChange={formik.handleChange}
                error={formik.touched.effective_end && !!formik.errors.effective_end}
                helperText={formik.touched.effective_end && formik.errors.effective_end}
                data-testid="new-policy-effective-end"
              />
            </Stack>
            <TextField
              required
              type="date"
              name="electable_start_date"
              label="Electable start (open enrollment)"
              slotProps={{ inputLabel: { shrink: true } }}
              value={formik.values.electable_start_date}
              onChange={formik.handleChange}
              error={formik.touched.electable_start_date && !!formik.errors.electable_start_date}
              helperText={formik.touched.electable_start_date && formik.errors.electable_start_date}
              data-testid="new-policy-electable-start"
            />
            <TextField
              name="description"
              label="Description (optional)"
              value={formik.values.description}
              onChange={formik.handleChange}
              data-testid="new-policy-description"
            />
            <ExistingDraftNotice
              open={open}
              contractId={
                formik.values.contract === '' ? undefined : Number(formik.values.contract)
              }
            />
            <Alert severity="info">
              The draft opens with its outstanding items listed. A plan id and the accumulator grid
              are needed before it can be published.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            data-testid="new-policy-submit"
          >
            Create draft
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
